import { IconDotsVertical } from '@tabler/icons-react'

import { logoutUser } from '@/api/authApi'
import { useState, useMemo } from 'react'
import { UserActionDialog } from './UserActionDialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { PATHROUTES } from '@/constants/path'
import { tokenService } from '@/lib/token.service'
import { useNavigate } from 'react-router'
import { LogoutIcon, UserIcon } from './Icons'
import { Button } from './ui/button'
import { UserRoundCog, Check } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { USER_QUERY_KEYS } from '@/api/queryKeys'
import type { User, UserRole } from '@/schemas/user.schema'
import { getUserProfile } from '@/api/userApi'
import { USER_ROLES } from '@/constants'
import { useUserRole } from '@/contexts/UserRoleContext'

const ROLE_ROUTE_MAP: Record<string, string> = {
  super_admin: PATHROUTES.DASHBOARD.ROOT,
  event_admin: PATHROUTES.DASHBOARD.EVENT_DASHBOARD,
  sponsor: PATHROUTES.DASHBOARD.SPONSORSHIP_MANAGEMENT,
}

export function NavUser({ user }: Readonly<{ user: User }>) {
  const { isMobile } = useSidebar()
  const [showLogoutDialog, setShowLogoutDialog] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const navigate = useNavigate()
  const { currentRole, updateUserRole } = useUserRole()
  const currentBaseRole = tokenService.getUser()?.userRoles?.[0]
  const queryClient = useQueryClient()

  const userRole = currentRole && currentRole.id ? currentRole : currentBaseRole

  const { data: userData } = useQuery({
    queryKey: USER_QUERY_KEYS.all,
    queryFn: getUserProfile,
  })

  const userRoles: User = userData

  const eligibleRoles = useMemo(() => {
    return (userRoles?.userRoles || []).filter(
      role => role.name !== USER_ROLES.attendee
    )
  }, [userRoles?.userRoles])

  const showRoleChangeMenu = eligibleRoles.length > 1

  const handleRoleChange = (selectedRole: UserRole) => {
    if (userRole?.name === selectedRole?.name) return

    updateUserRole(selectedRole)
    queryClient.invalidateQueries()

    const redirectPath =
      ROLE_ROUTE_MAP[selectedRole?.name || ''] || PATHROUTES.DASHBOARD.ROOT
    navigate(redirectPath)
  }

  const handleLogout = async () => {
    setIsLoggingOut(true)
    try {
      await logoutUser()
      tokenService.clear()
      window.location.href = '/auth/login'
    } finally {
      setIsLoggingOut(false)
      setShowLogoutDialog(false)
    }
  }

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground border border-ss-neutral-white bg-ss-neutral-white/60 rounded-xl flex items-center gap-3 p-2 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors w-full h-auto text-ss-action-gray-200"
                aria-label="Open user menu"
                tabIndex={0}
              >
                <Avatar className="size-11 rounded-b-full">
                  <AvatarImage
                    src={user?.profilePicture?.url}
                    alt={`Avatar for ${user.firstName}`}
                  />
                  <AvatarFallback className="rounded-lg grayscale">
                    {`${user?.firstName?.toUpperCase()[0]}${user?.lastName?.toUpperCase()[0]}`}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{`${user?.firstName} ${user?.lastName}`}</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {user.email}
                  </span>
                </div>
                <IconDotsVertical
                  className="ml-auto size-4 rotate-90"
                  aria-hidden="true"
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="min-w-24 rounded-2xl"
              side={isMobile ? 'bottom' : 'top'}
              align="end"
              sideOffset={4}
              aria-label="User menu"
            >
              <DropdownMenuGroup>
                <DropdownMenuItem
                  onClick={() => navigate(PATHROUTES.DASHBOARD.USER_PROFILE)}
                  aria-label="profile"
                  className="cursor-pointer pr-5 text-left text-sm font-medium"
                >
                  <UserIcon aria-hidden="true" />
                  My Profile
                </DropdownMenuItem>
                {showRoleChangeMenu && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <UserRoundCog className="mr-2 text-ss-neutral-900 size-4" />
                      Change Role
                    </DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent>
                        {eligibleRoles.map(role => {
                          const isCurrentRole = userRole?.name === role.name
                          return (
                            <DropdownMenuItem
                              key={role.id}
                              onClick={() => handleRoleChange(role)}
                              disabled={isCurrentRole}
                              className={
                                isCurrentRole
                                  ? 'bg-ss-neutral-50/60 hover:bg-ss-neutral-50/60 cursor-not-allowed flex items-center justify-between'
                                  : 'cursor-pointer flex items-center justify-between'
                              }
                            >
                              {role.title}
                              {isCurrentRole && (
                                <Check className="ml-2 size-4 text-ss-neutral-900" />
                              )}
                            </DropdownMenuItem>
                          )
                        })}
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                )}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowLogoutDialog(true)}
                className="cursor-pointer text-ss-critical-200 text-sm font-medium"
                aria-label="Log out"
              >
                <LogoutIcon className="" aria-hidden="true" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <UserActionDialog
        open={showLogoutDialog}
        onOpenChange={setShowLogoutDialog}
        title="Are you sure you want to log out of your account?"
        subtext="You’ll need to enter your credentials again to log back in."
        confirmButtonText="Logout"
        danger={true}
        showForm={false}
        isLoading={isLoggingOut}
        onConfirm={handleLogout}
      />
    </>
  )
}
